import Dexie, { type EntityTable } from 'dexie';
import type {
  CachedRoundAssignment,
  CachedShot,
  OutboxEntry,
  SyncMeta,
  SyncStore,
} from './store.js';

export class FlmDexie extends Dexie {
  outbox!: EntityTable<OutboxEntry, 'id'>;
  rounds!: EntityTable<CachedRoundAssignment, 'id'>;
  shots!: EntityTable<CachedShot, 'id'>;
  meta!: EntityTable<SyncMeta, 'key'>;

  constructor() {
    super('flm9');
    this.version(1).stores({
      outbox: 'id, status, createdAt, op',
      rounds: 'id, refereeUserId, roundId',
      shots: 'id, roundAssignmentId, [roundAssignmentId+ord], clientIdempotencyKey',
      meta: 'key',
    });
  }
}

let _db: FlmDexie | null = null;
export function getLocalDb(): FlmDexie {
  if (!_db) _db = new FlmDexie();
  return _db;
}

/**
 * SyncStore backed by the Dexie/IndexedDB database above. Wraps the same
 * `getLocalDb()` singleton the referee screens read directly, so reconcile
 * writes and direct keypad reads see one consistent store.
 */
export class DexieSyncStore implements SyncStore {
  constructor(private readonly db: FlmDexie = getLocalDb()) {}

  transaction<T>(work: (store: SyncStore) => Promise<T>): Promise<T> {
    return this.db.transaction(
      'rw',
      this.db.outbox,
      this.db.shots,
      this.db.meta,
      this.db.rounds,
      async () => await work(this),
    );
  }

  addOutbox(entry: OutboxEntry): Promise<void> {
    return this.db.outbox.add(entry).then(() => undefined);
  }

  firstPending(): Promise<OutboxEntry | undefined> {
    // Preserves the original drainer behaviour (`.where('status').equals(...).first()`),
    // which Dexie returns in primary-key (uuid v7 ⇒ time-ordered) order.
    return this.db.outbox.where('status').equals('pending').first();
  }

  updateOutbox(id: string, patch: Partial<OutboxEntry>): Promise<void> {
    return this.db.outbox.update(id, patch).then(() => undefined);
  }

  listPendingAndInflight(): Promise<OutboxEntry[]> {
    return this.db.outbox
      .where('status')
      .anyOf('pending', 'inflight')
      .toArray()
      .then((rows) => rows.sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id)));
  }

  listFailedTerminal(): Promise<OutboxEntry[]> {
    return this.db.outbox.where('status').equals('failed_terminal').toArray();
  }

  shotsForAssignment(assignmentId: string): Promise<CachedShot[]> {
    return this.db.shots.where('roundAssignmentId').equals(assignmentId).toArray();
  }

  async replaceShotsForAssignment(assignmentId: string, shots: CachedShot[]): Promise<void> {
    await this.db.transaction('rw', this.db.shots, async () => {
      await this.db.shots.where('roundAssignmentId').equals(assignmentId).delete();
      if (shots.length > 0) await this.db.shots.bulkAdd(shots);
    });
  }

  putShot(shot: CachedShot): Promise<void> {
    return this.db.shots.put(shot).then(() => undefined);
  }

  async deleteShotByKey(assignmentId: string, clientIdempotencyKey: string): Promise<void> {
    await this.db.shots
      .where('[roundAssignmentId+ord]')
      .between([assignmentId, Dexie.minKey], [assignmentId, Dexie.maxKey])
      .filter((s) => s.clientIdempotencyKey === clientIdempotencyKey)
      .delete();
  }

  putRound(round: CachedRoundAssignment): Promise<void> {
    return this.db.rounds.put(round).then(() => undefined);
  }

  getRound(id: string): Promise<CachedRoundAssignment | undefined> {
    return this.db.rounds.get(id);
  }

  async getMeta(key: string): Promise<string | undefined> {
    const row = await this.db.meta.get(key);
    return row?.value;
  }

  setMeta(key: string, value: string): Promise<void> {
    return this.db.meta.put({ key, value }).then(() => undefined);
  }
}

let _dexieStore: DexieSyncStore | null = null;
/** Lazy Dexie-backed store singleton — the default for the web PWA. */
export function getDexieStore(): DexieSyncStore {
  if (!_dexieStore) _dexieStore = new DexieSyncStore(getLocalDb());
  return _dexieStore;
}
